import {
    isString as _isString,
    isInteger as _isInteger,
    isObject as _isObject,
    isFunction as _isFunction,
    uniq as _uniq,
    isArray as _isArray,
    isNil as _isNil,
    isEqual as _isEqual,
    get as _get,
} from 'lodash';
import Moment from 'moment';

import { ParamsSerializer } from '../data/auth-service';

const help = {
    Type: (obj, type) => {
        return Object.prototype.toString.call(obj).toLowerCase().includes(type);
    },
    ModalStyle: () => {
        const top = 50;
        const left = 50;

        return {
            top: `${top}%`,
            left: `${left}%`,
            transform: `translate(-${top}%, -${left}%)`,
        };
    },
    formatBytes: (bytes, decimals = 1, speed) => {
        if (bytes === 0) return (speed ? '0 B/s' : '0 Bytes');

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i] + (speed ? '/s' : '');
    },
    capitalize: (word) => {
        return word.charAt(0).toUpperCase() + word.slice(1);
    },
    UniqueID: () => {
        return '_' + Math.random().toString(36).substr(2, 9);
    },
};

// TODO: add unit tests
function Flatten ({ object, path = '', delimiter = '&' }) {
    if (object == null) return '';
    if (Array.isArray(object)) return object.map(v => Flatten({ object: v, path, delimiter }));
    if (typeof object === 'object') {
        return Object.entries(object).reduce((a, [k, v]) => {
            return !Array.isArray(v) && typeof v === 'object'
                ? { ...a, ...Flatten({ object: v, path: `${path}${k}${delimiter}`, delimiter }) }
                : { ...a, [`${path}${k}`]: Flatten({ object: v, path, delimiter }) };
        }, {});
    }
    return object;
}

function MtdtMerge (dv, sv, key, object, source, stack) {
    if (_isNil(sv)) return dv; // do not over-write good values with empties
    if (_isArray(dv)) return _uniq(dv.concat(sv));
    if (_isInteger(dv) && _isInteger(sv) && sv !== dv) return [dv, sv];
    if (_isString(dv) && _isString(sv) && sv !== dv) return [dv, sv];
}

function IsRealObject (o) {
    return _isObject(o) && !_isArray(o) && !_isFunction(o);
}

// converts acronyms to uppercase. e.g. from: {{ 'proto_http' | acronym }} to: 'proto HTTP'
function Acronym (input) {
    const acronyms = {
        md5: 'MD5',
        ipv4: 'IPv4',
        ipv6: 'IPv6',
        uuid: 'UUID',
        sip: 'SIP',
        ftp: 'FTP',
        http: 'HTTP',
        smtp: 'SMTP',
        sql: 'SQL',
        dns: 'DNS',
        aim: 'AIM',
        dnp3: 'DNP3',
        enip: 'EN/IP',
        ssh: 'SSH',
        ssl: 'SSL',
        dpd: 'DPD',
        dhcp: 'DHCP',
        ja3: 'JA3',
        ja3s: 'JA3 Server',
    };
    if (typeof input === 'string') {
        return input.split(/[-_ ]/).map(key => {
            if (!key || key.length === 0) return '';
            if (key.length === 2) return key.toUpperCase();
            return acronyms[key.toLowerCase()] || key;
        }).join(' ');
    }
    return '';
}

function ArrayFromString (s) {
    if (Array.isArray(s)) return s;
    const [err, ret] = SafeFromJson(s);
    return err
        ? s === undefined ? [] : [s]
        : Array.isArray(ret)
            ? ret : ret === undefined ? [] : [ret];
}

function ToArray (d) {
    if (d === undefined) return [];
    return Array.isArray(d) ? d : ((d + '').split(',').map(v => v.trim()));
}

function SafeFromJson (s) {
    try {
        return [null, JSON.parse(s)];
    } catch (err) {
        return [err];
    }
}

function SaveFile (data, filename, type) {
    const mime = type || 'application/octet-stream';
    const b = new window.Blob([data], { type });
    if (window.navigator.msSaveOrOpenBlob) {
        navigator.msSaveBlob(b, filename);
    } else {
        var link = document.createElement('a');
        let webkit = /AppleWebKit\/([\d.]+)/.exec(navigator.userAgent);
        webkit = webkit ? parseFloat(webkit[1]) : 0;
        if (typeof safari !== 'undefined' && webkit < 603.1 /* Safari 10.1 */) {
            link.href = 'data:' + mime + 'base64,' + btoa(String.fromCharCode.apply(String,
                new Uint8Array(data)));
        } else {
            link.href = window.URL.createObjectURL(b);
        }
    }
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    // setTimeout(function () {
    //     document.body.removeChild(link);
    //     window.URL.revokeObjectURL(link.href);
    // }, 4200);
}

// stream a large file from server and save as
function SaveFileFromAPI (url, params) {
    const query = ParamsSerializer(params);
    // add a temporary hidden link
    const link = document.createElement('a');
    link.css = { display: 'none' };
    link.type = 'blob';
    link.href = url + '?' + query;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    // remove temporary link
    link.remove();
}

function TimeDiffLong (v) {
    const d = Math.floor(v / 86400);
    const h = Math.floor((v % 86400) / 3600);
    const m = Math.floor((v % 3600) / 60);
    const s = Math.floor((v % 60));
    const ret = [];
    if (d) ret.push(`${d + ' day' + (d > 1 ? 's' : '')}`);
    if (h) ret.push(`${h + ' hr'}`);
    if (m) ret.push(`${m + ' min'}`);
    if (s) ret.push(`${s + ' sec'}`);
    return ret.join(', ');
}

function Pad (num, size) {
    const s = '000000000' + num;
    return s.substr(s.length - size);
}

function TimeDiffShort (v) {
    const h = Math.floor((v % 86400) / 3600);
    const m = Math.floor((v % 3600) / 60);
    const s = Math.floor((v % 60));
    return `${Pad(h, 2)}:${Pad(m, 2)}:${Pad(s, 2)}`;
}

function GetEpochDateFromQuick (q) {
    switch (q) {
        case '2week':
            return Moment().subtract(14, 'days').utc().valueOf();
        case 'week':
            return Moment().subtract(7, 'days').utc().valueOf();
        case 'day':
            return Moment().subtract(24, 'hours').utc().valueOf();
        default:
            return Moment().subtract(1, 'hours').utc().valueOf();
    }
}

function GetISODateFromQuick (q) {
    switch (q) {
        case '2week':
            return Moment().subtract(14, 'days').utc().toISOString();
        case 'week':
            return Moment().subtract(7, 'days').utc().toISOString();
        case 'day':
            return Moment().subtract(24, 'hours').utc().toISOString();
        default:
            return Moment().subtract(1, 'hours').utc().toISOString();
    }
}

// https://stackoverflow.com/a/30810322/5895550
function CopyToClipboard (text) {
    // navigator.clipboard.writeText fails with longer text
    const previousFocusElement = document.activeElement;
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        document.execCommand('copy');
    } catch (err) {
        console.error('could not copy text,', err);
    } finally {
        previousFocusElement.focus();
    }

    document.body.removeChild(textArea);
}

function getFriendlyValue (variable, value, data) {
    if (!data.map[variable]) {
        console.debug('getFriendlyValue cannot find variable', variable, data.map);
        return '';
    }
    if (data.map[variable].title === 'Sensor') {
        return _get(data.sensorNames, `${value}.name`, value);
    }
    if (data.map[variable].type === 'ip' || data.map[variable].type === 'ips') {
        return _get(data.assets, `${value}.name`, value);
    }
    if (data.map[variable].type === 'bt_job_uuid') {
        return _get(data.jobs, `${value}.name`, value);
    }
    return value;
}

// 'filter', 'map' friendly variable names, 'html' for bold operators, 'not' is set by the recursion
// data = { map, sensorNames, assets, backtetingJobs }
function FilterToEnglish (f, data, html, not) {
    const bold = s => (html ? `<b>${s}</b>` : s);
    const span = s => (html ? `<span>${s}</span>` : s);

    if (!f || f.disabled) return span('');

    if (f.variable) {
        // translate sensor/asset names
        const value = getFriendlyValue(f.variable, f.value, data);
        const is = not ? `is ${bold('NOT')}` : 'is';
        const v = data.map[f.variable] || {};
        return (f.operator || '') === 'exists'
            ? `${v.title || f.variable} ${bold(not ? 'is empty' : 'exists')}`
            : `${v.title || f.variable} ${is} '${value}'`;
    }
    let o = (f.operator || '').toLowerCase();
    const nodes = (f.nodes || [])
        .filter(n => n && !n.disabled)
        .map(n => FilterToEnglish(n, data, html, o === 'not'))
        .filter(n => n.length > 0);
    o = o === 'not' ? 'AND' : o.length > 0 ? o.toUpperCase() : ', ';
    return span(nodes.join(` ${bold(o)} `));
}

function IsEmpty (object, ignoreZero) {
    if (!object || object === '' || object === undefined || (!ignoreZero && object === 0) ||
        typeof object === 'undefined') {
        return true;
    }

    for (const k in object) {
        if (Object.prototype.hasOwnProperty.call(object, k)) {
            return false;
        }
    }

    if (_isEqual({}, object)) {
        return true;
    }

    if (typeof object === 'object' && object != null) {
        return object.length !== undefined && object.length === 0;
    }

    // e.g. 5 is not empty
    return typeof object === 'object';
}

function TranslateHome (home) {
    let h = home || '/';
    switch (home) {
        case '/dashboard':
            h = '/';
            break;
        case '/alerts':
            h = '/views/alerts';
            break;
        case '/metadata':
            h = '/views/metadata';
            break;
        case '/sensors':
            h = '/sensors';
            break;
        case '/users':
            h = '/system/users_roles';
            break;
        case '/audit':
            h = '/system/audit';
            break;
        case '/cmc':
            h = '/cmc';
            break;
    }

    return h;
}

function TranslateTheme (theme) {
    let t = 'dark';

    // LIGHT ::: light, tempest
    // DARK ::: brown, pink, purple, obsidian
    switch (theme) {
        case 'light':
            t = 'light';
            break;
        case 'tempest':
            t = 'light';
            break;
        case 'pink':
            t = 'pink';
    }

    return t;
}

// reducer to update one config settings without losing the rest + noise reduction if no update
function UpdateReducer (current, setting) {
    // update render reduction
    const p = JSON.stringify(setting);
    const n = JSON.stringify(current);
    if (p !== n) {
        return { ...(current || {}), ...setting };
    }
    return current;
}

// like the update reducer, but this guy removes attributes that aren't in the new object
function UpdateAllReducer (current, setting) {
    // update render reduction
    const p = JSON.stringify(setting);
    const n = JSON.stringify(current);
    if (p !== n) {
        return setting;
    }
    return current;
}

function IsSameMappings (o, n) {
    if (Object.entries(n || {}).length !== Object.entries(o || {}).length) return false;
    return !Object.entries(n || {})
        .some(([k, v]) => ((v || []).join('') !== (o[k] || []).join('')));
}

function FriendlyPolicyName (policyName) {
    switch (policyName) {
        case 'bro_ti':
            return 'Zeek Scripts';
        case 'bt_bro_ti':
            return 'BT Zeek Scripts';
        case 'bt_suricata_manual':
            return 'BT Suricata Rules';
        case 'btconf':
            return 'Backtesting Configuration';
        case 'custom_geoip':
            return 'Geo IP';
        case 'data_tags':
            return 'Data Tuning';
        case 'eexportd_conf':
            return 'Exports';
        case 'file_hash':
            return 'File Hash';
        case 'pcap_config':
            return 'PCAP Configuration';
        case 'sensor_upgrade':
            return 'Sensor Upgrade';
        case 'suricata_config':
            return 'Suricata Vars';
        case 'suricata_iprep':
            return 'Suricata IP Rep';
        case 'suricata_rules':
            return 'Suricata Rules';
        case 'system_config':
            return 'System Configuration';
        default:
            return '';
    }
}

// defense against the dark arts of lazy coders
const GetBTUUID = (data, path) => {
    let btuuid = _get(data, path, [EMPTY_UUID]);
    if (!btuuid) return undefined;

    if (!Array.isArray(btuuid)) btuuid = [btuuid];
    return btuuid.length < 1 ? undefined
        : btuuid[0] === EMPTY_UUID ? undefined : btuuid[0];
};

const EMPTY_UUID = '00000000-0000-0000-0000-000000000000';

// Because javascript sucks >:(
const And = (a, b) => {
    const upper = 0x80000000;
    const lower = 0x7fffffff;

    const u = ~~(a / upper) & ~~(b / upper);
    const l = (a & lower) & (b & lower);

    return (u * upper) + l;
};

const IsValidDate = v => {
    if (!v) {
        return false;
    }

    // defense against the dark arts of zero dates
    const mv = Moment(v);
    return mv.isValid() && mv.unix() > 0;
};

const GenerateCodeChallengeS256 = (codeVerifier) => {
    const encoder = new TextEncoder();
    const data = encoder.encode(codeVerifier);
    return crypto.subtle.digest('SHA-256', data).then(buffer => {
        return btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)))
            .replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    });
};

const InitCase = (s) => {
    return s.toLowerCase()
        .split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const NoKeyword = key =>
    key && key.indexOf('.keyword') > 0
        ? key.substring(0, key.indexOf('.keyword'))
        : key;

const IsFloat = f => {
    if (typeof f === 'number' && !isNaN(f) && f % 1 !== 0) {
        return true;
    }
    return false;
};

const ToFixed = (value, fixed) => {
    if (typeof value === 'number') {
        return value.toFixed(fixed);
    } else if (!isNaN(parseInt(value))) {
        return parseInt(value).toFixed(2);
    }
    return value;
};

const RuleTitle = (title) => {
    return title.replaceAll('-', ' ').replaceAll('_', ' ');
};

export {
    help as default,
    Flatten,
    MtdtMerge,
    IsRealObject,
    Acronym,
    ArrayFromString,
    SafeFromJson,
    SaveFile,
    SaveFileFromAPI,
    TimeDiffLong,
    TimeDiffShort,
    Pad,
    GetEpochDateFromQuick,
    GetISODateFromQuick,
    CopyToClipboard,
    FilterToEnglish,
    IsEmpty,
    TranslateHome,
    TranslateTheme,
    UpdateReducer,
    UpdateAllReducer,
    IsSameMappings,
    FriendlyPolicyName,
    ToArray,
    ToFixed,
    RuleTitle,
    EMPTY_UUID,
    GetBTUUID,
    And,
    IsValidDate,
    GenerateCodeChallengeS256,
    InitCase,
    NoKeyword,
    IsFloat,
};
